import {
  ArrowRightLeft,
  Bell,
  CalendarClock,
  CircleDot,
  FileCheck,
  MessageSquare,
  PencilLine,
  Target,
  TrendingUp,
  UserPlus,
} from "lucide-react"
import { comoFechaHora } from "@/lib/fechas"

/**
 * La lista de avisos, compartida por la campanita y por la pantalla completa.
 *
 * ⚠ **El texto llega redactado del servidor.** No se recompone aquí a partir del
 * tipo: si la pantalla armara la frase, un aviso de hace un mes cambiaría de
 * redacción al cambiar el código y diría cosas que no pasaron. El tipo sólo elige el
 * icono.
 *
 * ⚠ **Lo no leído se distingue por dos cosas, no por una.** Color de fondo y un
 * punto: el color solo no llega a quien no lo distingue, y esto es exactamente el
 * caso en que la diferencia es el contenido (WCAG 1.4.1).
 */

export type TipoAviso =
  | "TAREA_ASIGNADA"
  | "TAREA_ESTADO"
  | "TAREA_AVANCE"
  | "TAREA_CAMBIO"
  | "TAREA_COMENTARIO"
  | "TAREA_VENCE"
  | "ORDENES_GENERADAS"
  | "ORDEN_PAGO"
  | "PRESUPUESTO"

export type Aviso = {
  id: number
  tipo: TipoAviso
  actor_id: number
  tarea_id: number | null
  titulo: string
  cuerpo: string | null
  leido_en: string | null
  creado_en: string
}

const ICONO: Record<string, typeof Bell> = {
  TAREA_ASIGNADA: UserPlus,
  TAREA_ESTADO: ArrowRightLeft,
  TAREA_AVANCE: TrendingUp,
  TAREA_CAMBIO: PencilLine,
  TAREA_COMENTARIO: MessageSquare,
  TAREA_VENCE: CalendarClock,
  ORDENES_GENERADAS: FileCheck,
  ORDEN_PAGO: FileCheck,
  PRESUPUESTO: Target,
}

/**
 * A dónde lleva pulsar un aviso. `null` si no lleva a ningún sitio.
 *
 * ⚠ **Las órdenes de pago van a su bandeja, no a una ficha**: el aviso no guarda el
 * id de la orden —`avisos` sólo enlaza tareas— y el código va en el título, que es
 * lo que se busca en la bandeja. Un sitio, y no dos copias, para decidirlo: lo usan
 * la campanita y la pantalla de avisos.
 */
export function destinoDe(aviso: Aviso): string | null {
  if (aviso.tarea_id !== null) return `/tareas?tarea=${aviso.tarea_id}`
  if (aviso.tipo === "ORDEN_PAGO" || aviso.tipo === "ORDENES_GENERADAS") {
    return "/administracion/ordenes-pago"
  }
  // Una partida en alerta lleva al presupuesto, donde se ve cuál y de qué se compone.
  if (aviso.tipo === "PRESUPUESTO") return "/administracion/presupuesto"
  return null
}

function cuando(iso: string): string {
  return comoFechaHora(iso)
}

type Props = {
  avisos: Aviso[]
  onAbrir: (aviso: Aviso) => void
  /** En la campanita cabe menos: se recorta el cuerpo a una línea. */
  compacta?: boolean
}

export function ListaDeAvisos({ avisos, onAbrir, compacta = false }: Props) {
  if (avisos.length === 0) {
    return (
      <p className="p-4 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        No hay avisos.
      </p>
    )
  }

  return (
    <ul className="space-y-1">
      {avisos.map((a) => {
        // Con reserva: un tipo que el servidor ya manda y esta pantalla todavia no
        // conoce dejaria `Icono` en `undefined`, y React tumba la campanita entera
        // por un icono. Un aviso con el icono generico se lee igual de bien.
        const Icono = ICONO[a.tipo] ?? Bell
        const sinLeer = a.leido_en === null
        return (
          <li key={a.id}>
            <button
              type="button"
              onClick={() => onAbrir(a)}
              data-testid={`aviso-${a.id}`}
              className="flex w-full items-start gap-2 rounded-[var(--radius-control)] p-2 text-left toque-cristal"
              style={{ backgroundColor: sinLeer ? "var(--surface-elevated)" : undefined }}
            >
              <span className="mt-0.5 shrink-0" style={{ color: "var(--fg-muted)" }}>
                <Icono size={14} aria-hidden={true} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-medium">{a.titulo}</span>
                  {sinLeer && (
                    <CircleDot
                      size={10}
                      aria-label="Sin leer"
                      style={{ color: "var(--primary-texto)" }}
                    />
                  )}
                </span>
                {a.cuerpo !== null && (
                  <span
                    className={`block text-xs ${compacta ? "truncate" : ""}`}
                    style={{ color: "var(--fg-muted)" }}
                  >
                    {a.cuerpo}
                  </span>
                )}
                <span className="block text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                  {cuando(a.creado_en)}
                </span>
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
